import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import './GotokiButton.css';
import { GOTOKI_PUBLIC_KEY, GOTOKI_PLAY_IMAGE, GOTOKI_STOP_IMAGE, GOTOKI_BUTTON_SIZE } from '../../../constants/gotoki';

const GotokiButton = forwardRef(({ onCallStatusChange, onProductRecommendation, onProductRecommendationLoading, onHeadphoneTypeSelection, onHeadphoneTypeLoading, onMessage, onCallEnd }, ref) => {
  const gotokiButtonRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const { translate, currentLanguage, getAssistantId } = useLanguage();

  // Dynamic assistant ID based on language
  const ASSISTANT_ID = getAssistantId();

  // Update button attributes when language changes
  useEffect(() => {
    const button = gotokiButtonRef.current;
    if (!button) return;

    button.setAttribute('assistant-id', getAssistantId());
    button.setAttribute('start-button-text', translate('startButtonText'));
    button.setAttribute('stop-button-text', translate('stopButtonText'));
    button.setAttribute('connecting-text', translate('connecting'));
  }, [currentLanguage, translate, getAssistantId]);

  // Handle message events from gotoki-button
  useEffect(() => {
    const button = gotokiButtonRef.current;
    if (!button) return;

    const handleMessage = (event) => {
      try {
        let message = event.detail;

        // Handle array wrapper
        if (Array.isArray(message)) {
          if (message.length === 0) return;
          message = message[0];
        }

        // Validate message structure
        if (!message || typeof message !== 'object') {
          console.warn('[Gotoki] Invalid message format:', message);
          return;
        }

        console.log('[Gotoki] Received message:', message);

        // Notify parent of any incoming message
        if (onMessage) {
          onMessage(message);
        }

        // Handle tool_result_loading 
        const loadingPayload =
          message.type === 'tool_result_loading'
            ? message
            : message.data?.type === 'tool_result_loading'
              ? message.data
              : null;

        // tool_name may be suffixed with a call id (e.g. "showHeadphoneTypes__180"), match by prefix
        const loadingToolName = loadingPayload?.tool_name?.split('__')[0];
        if (loadingToolName === 'showHeadphoneTypes') {
          console.log('[Gotoki] Headphone types loading');
          onHeadphoneTypeLoading?.();
          return;
        }
        if (loadingToolName === 'showProductRecommendation') {
          console.log('[Gotoki] Product recommendation loading');
          onProductRecommendationLoading?.();
          return;
        }

        // Handle call_end event
        if (message.type === 'call_end') {
          console.log('[Gotoki] Call ended:', message.reason, '-', message.message);
          if (onCallEnd) {
            onCallEnd({
              reason: message.reason,
              message: message.message
            });
          }
          return;
        }

        // Extract tool_calls
        const toolCalls = message.tool_calls || message.toolCalls || [];

        if (!Array.isArray(toolCalls)) {
          return;
        }

        // Process tool calls
        for (const toolCall of toolCalls) {
          const functionName = toolCall.function?.name || toolCall.name;

          if (functionName === 'showProductRecommendation') {
            try {
              const argsRaw = toolCall.function?.arguments || toolCall.arguments;
              const args = typeof argsRaw === 'string' ? JSON.parse(argsRaw) : argsRaw;

              console.log('[Gotoki] Product recommendation:', args);

              if (args.primaryProduct && onProductRecommendation) {
                onProductRecommendation({
                  primaryProduct: args.primaryProduct,
                  secondaryProducts: args.alternatives || []
                });
              }
            } catch (parseError) {
              console.error('[Gotoki] Error parsing tool call:', parseError);
            }
          } else if (functionName === 'showHeadphoneTypes') {
            try {
              const argsRaw = toolCall.function?.arguments || toolCall.arguments;
              const args = typeof argsRaw === 'string' ? JSON.parse(argsRaw) : argsRaw;

              console.log('[Gotoki] Headphone type selection:', args);

              if (args.recommendedTypes && onHeadphoneTypeSelection) {
                onHeadphoneTypeSelection({
                  recommendedTypes: args.recommendedTypes
                });
              }
            } catch (parseError) {
              console.error('[Gotoki] Error parsing showHeadphoneTypes tool call:', parseError);
            }
          }
        }
      } catch (error) {
        console.error('[Gotoki] Error handling message:', error);
      }
    };

    button.addEventListener('message', handleMessage);

    return () => {
      button.removeEventListener('message', handleMessage);
    };
  }, [onProductRecommendation, onProductRecommendationLoading, onHeadphoneTypeSelection, onHeadphoneTypeLoading, onMessage, onCallEnd]);

  // Track connection state (if gotoki-button exposes it)
  useEffect(() => {
    const button = gotokiButtonRef.current;
    if (!button) return;

    // Try to listen for state change events
    const handleStateChange = (event) => {
      const newState = event.detail?.state || event.detail;
      const connected = newState === 'connected' || newState === true;
      setIsConnected(connected);
      onCallStatusChange?.(connected);
    };

    const handleCallStart = () => {
      console.log('[Gotoki] Call started');
      setIsConnected(true);
      onCallStatusChange?.(true);
    };

    const handleCallEnd = () => {
      console.log('[Gotoki] Call ended');
      setIsConnected(false);
      onCallStatusChange?.(false);
      onCallEnd?.({
        reason: 'call_disconnected',
        message: 'Call ended'
      });
    };

    button.addEventListener('statechange', handleStateChange);
    button.addEventListener('call-start', handleCallStart);
    button.addEventListener('call-end', handleCallEnd);

    return () => {
      button.removeEventListener('statechange', handleStateChange);
      button.removeEventListener('call-start', handleCallStart);
      button.removeEventListener('call-end', handleCallEnd);
    };
  }, [onCallStatusChange, onCallEnd]);

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    startCall: () => {
      const button = gotokiButtonRef.current;
      if (button && !isConnected) {
        console.log('[Gotoki] Starting call via ref');
        button.click();
      }
    },
    endCall: () => {
      const button = gotokiButtonRef.current;
      if (button && isConnected) {
        console.log('[Gotoki] Ending call via ref');
        button.click();
      }
    },
    isConnected: () => isConnected
  }));

  return (
    <div className="flex flex-col items-center justify-center relative">
      <div className="headphone-wrapper relative flex items-center justify-center w-[250px] h-[250px] sm:w-[280px] sm:h-[280px] md:w-[320px] md:h-[320px] lg:w-[350px] lg:h-[350px] xl:w-[400px] xl:h-[400px] 2xl:w-[450px] 2xl:h-[450px]">
        <img
          src="/Assets/KI-Assistent_Expert_Kopfhörer.png"
          alt="Beyerdynamic KI Assistent"
          className="w-full h-full object-contain transition-all duration-300 hover:brightness-105 hover:scale-[1.02]"
        />
        <div className="absolute top-[46%] left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center z-10">
          <gotoki-button
            ref={gotokiButtonRef}
            public-key={GOTOKI_PUBLIC_KEY}
            assistant-id={ASSISTANT_ID}
            play-image={GOTOKI_PLAY_IMAGE}
            stop-image={GOTOKI_STOP_IMAGE}
            size={GOTOKI_BUTTON_SIZE}
            start-button-text={translate('startButtonText')}
            stop-button-text={translate('stopButtonText')}
            connecting-text={translate('connecting')}
          />
        </div>
      </div>
    </div>
  );
});

GotokiButton.displayName = 'GotokiButton';

export default GotokiButton;

